/** 链接和图片共用白名单；仅绑定会话鉴权读取器时允许文件图片，控制字符始终拒绝。 */
export function safeMarkdownUrl(url: string, key = 'href', allowSessionFiles = false): string {
  const value = url.trim();
  if (!value || /[\u0000-\u001f\u007f]/.test(value)) return '';
  // 网络相对链接是外部 HTTPS 资源，不能被本地文件预览器当作路径。
  if (value.startsWith('//')) return `https:${value}`;
  if (/^[A-Za-z]:[\\/]/.test(value)) return key === 'href' ? `file:///${value.replace(/\\/g, '/')}` : allowSessionFiles ? value : '';
  const protocol = /^([a-z][a-z0-9+.-]*):/i.exec(value)?.[1].toLowerCase();
  if (!protocol) return value;
  const allowed = key === 'src' ? ['http', 'https', ...(allowSessionFiles ? ['file'] : [])] : ['http', 'https', 'mailto', 'tel', 'file'];
  return allowed.includes(protocol) ? value : '';
}

/** 寻找未转义的公式结束符；未闭合流保持原文，等待后续增量。 */
const findClosingLatexDelimiter = (input: string, start: number, delimiter: '\\)' | '\\]'): number => {
  let cursor = start;
  while (cursor < input.length) {
    const found = input.indexOf(delimiter, cursor);
    if (found === -1) return -1;
    if (found === 0 || input[found - 1] !== '\\') return found;
    cursor = found + delimiter.length;
  }
  return -1;
};

/** 原样跳过完整或尚未闭合的代码围栏，避免把代码内的 LaTeX 当公式。 */
const copyFencedCodeBlock = (input: string, start: number): { text: string; next: number } | null => {
  if (start > 0 && input[start - 1] !== '\n') return null;
  const lineEnd = input.indexOf('\n', start);
  const firstLine = input.slice(start, lineEnd === -1 ? input.length : lineEnd);
  const match = firstLine.match(/^( {0,3})(`{3,}|~{3,})/);
  if (!match) return null;

  const fence = match[2];
  const fenceChar = fence[0];
  const fenceLength = fence.length;
  let cursor = lineEnd === -1 ? input.length : lineEnd + 1;

  while (cursor < input.length) {
    const nextLineEnd = input.indexOf('\n', cursor);
    const line = input.slice(cursor, nextLineEnd === -1 ? input.length : nextLineEnd);
    const closeMatch = line.match(new RegExp(`^ {0,3}\\${fenceChar}{${fenceLength},}\\s*$`));
    if (closeMatch) {
      const next = nextLineEnd === -1 ? input.length : nextLineEnd + 1;
      return { text: input.slice(start, next), next };
    }
    cursor = nextLineEnd === -1 ? input.length : nextLineEnd + 1;
  }

  return { text: input.slice(start), next: input.length };
};

/** 复用桌面公式分隔规则；只转换代码之外的 \(…\) 和 \[…\]。 */
export const normalizeLatexDelimiters = (content: string): string => {
  if (!content.includes('\\(') && !content.includes('\\[')) {
    return content;
  }

  let result = '';
  let cursor = 0;

  while (cursor < content.length) {
    const fencedBlock = copyFencedCodeBlock(content, cursor);
    if (fencedBlock) {
      result += fencedBlock.text;
      cursor = fencedBlock.next;
      continue;
    }

    if (content[cursor] === '`') {
      const match = content.slice(cursor).match(/^`+/);
      const ticks = match?.[0] ?? '`';
      const end = content.indexOf(ticks, cursor + ticks.length);
      if (end === -1) {
        result += content.slice(cursor);
        break;
      }
      result += content.slice(cursor, end + ticks.length);
      cursor = end + ticks.length;
      continue;
    }

    if (content[cursor] === '\\' && content[cursor + 1] === '\\') {
      result += '\\\\'; cursor += 2; continue;
    }

    if (content.startsWith('\\(', cursor)) {
      const end = findClosingLatexDelimiter(content, cursor + 2, '\\)');
      if (end !== -1) {
        result += `$${content.slice(cursor + 2, end)}$`;
        cursor = end + 2;
        continue;
      }
    }

    if (content.startsWith('\\[', cursor)) {
      const end = findClosingLatexDelimiter(content, cursor + 2, '\\]');
      if (end !== -1) {
        result += `$$\n${content.slice(cursor + 2, end)}\n$$`;
        cursor = end + 2;
        continue;
      }
    }

    result += content[cursor];
    cursor += 1;
  }

  return result;
};
